import { ResolvedOperand } from '../types'

const DEFAULT_AXIS = 14
// max UI offset/lookback of 50, plus the target bar and one previous-bar read
const MAX_AXIS = 52

const sideDepth = (side?: ResolvedOperand): number =>
  side ? Math.max(side.offset, side.lookback?.count ?? 0) : 0

/**
 * Mirrors PDS's minimum-history guard (patternUtils.ts:399-417): the deepest
 * bar any side touches, plus headroom for that bar's own previous-bar read.
 * The legacy previousCandle window only contributes when its path is actually
 * taken — enabled AND no per-side targeting (PDS prefers per-side depth, and
 * the UI leaves a disabled previousCandle object behind on deselect).
 */
export const requiredDepth = (
  lhs?: ResolvedOperand,
  rhs?: ResolvedOperand,
  previousCandle?: { enabled?: boolean; numberOfCandles?: number; candlesNeeded?: number }
): number => {
  const sideBased = Math.max(2, sideDepth(lhs) + 2, sideDepth(rhs) + 2)
  const hasSideTargeting =
    (lhs && (lhs.offset > 0 || lhs.lookback)) || (rhs && (rhs.offset > 0 || rhs.lookback))
  if (previousCandle?.enabled && !hasSideTargeting) {
    return Math.max(sideBased, (previousCandle.numberOfCandles ?? 1) + 2)
  }
  return sideBased
}

export interface BarPlan {
  axisLength: number
  now: number
  /** now − offset; throws when the bar would fall below index 1 */
  resolve(offset: number): number
}

export const planAxis = (maxDepth: number): BarPlan => {
  const axisLength = Math.max(DEFAULT_AXIS, Math.min(maxDepth, MAX_AXIS))
  const now = axisLength - 1
  return {
    axisLength,
    now,
    resolve(offset: number): number {
      const bar = now - offset
      if (bar < 1) {
        throw new Error(`bar offset ${offset} exceeds the ${axisLength}-bar axis`)
      }
      return bar
    },
  }
}
